import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';  // Add this import
import TransitionVideo from '../components/TransitionVideo';
import { Lock } from 'lucide-react';

type Chapter = {
    id: number;
    title: string;
};

type ChapterStatus = {
    id: number;
    isUnlocked: boolean;
    componentPath: string | null;
};

interface MapProps {
    onAreaClick: (index: number) => void;
    activeChapter: number | null;
    setActiveChapter: (id: number | null) => void;
}

type TableOfContentsProps = {
    onChapterClick: (id: number) => void;
    activeChapter: number | null;
    setActiveChapter: (id: number | null) => void;
};

const TableOfContents: React.FC<TableOfContentsProps> = ({ onChapterClick, activeChapter, setActiveChapter }) => {
    // Define chapter availability status
    const chapterStatus: ChapterStatus[] = [
        { id: 1, isUnlocked: true, componentPath: "/greek-text" },  // First chapter available
        { id: 2, isUnlocked: false, componentPath: null },
        { id: 3, isUnlocked: false, componentPath: null },
        { id: 4, isUnlocked: false, componentPath: null },
        { id: 5, isUnlocked: false, componentPath: null },
        { id: 6, isUnlocked: false, componentPath: null },
        { id: 7, isUnlocked: false, componentPath: null },
        { id: 8, isUnlocked: false, componentPath: null },
        { id: 9, isUnlocked: false, componentPath: null },
        { id: 10, isUnlocked: false, componentPath: null },
        { id: 11, isUnlocked: false, componentPath: null },
        { id: 12, isUnlocked: false, componentPath: null },
    ];

    const chapters: Chapter[] = [
        { id: 1, title: "Το Λοντάρι της Νεμέας" },
        { id: 2, title: "Η Λερναία Ύδρα" },
        { id: 3, title: "Το Ελάφι της Άρτεμης" },
        { id: 4, title: "Ο Ερυμάνθιος Κάπρος" },
        { id: 5, title: "Οι Στάβλοι του Αυγεία" },
        { id: 6, title: "Οι Στυμφαλίδες Όρνιθες" },
        { id: 7, title: "Ο Ταύρος του Μίνωα" },
        { id: 8, title: "Τα Άλογα του Διομήδη" },
        { id: 9, title: "Η Ζώνη της Ιππολύτης" },
        { id: 10, title: "Τα Βόδια του Γηρυόνη" },
        { id: 11, title: "Τα Μήλα των Εσπερίδων" },
        { id: 12, title: "Ο Κέρβερος" },
    ];

    return (
        <div className="fixed top-0 right-0 h-full w-[30%] bg-stone-light text-greek-black p-6 shadow-lg font-cinzel flex flex-col">
            <h2 className="text-2xl font-bold mb-6 text-terracotta border-b-2 border-terracotta pb-2">12 Labors</h2>
            <ul className="flex-grow flex flex-col justify-between">
                {chapters.map((chapter) => {
                    const status = chapterStatus.find(s => s.id === chapter.id);
                    return (
                        <li
                            key={chapter.id}
                            className={`hover:bg-warm-cream rounded-lg transition-colors duration-200 
                                ${activeChapter === chapter.id ? 'bg-warm-cream' : ''}
                                ${!status?.isUnlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onMouseEnter={() => setActiveChapter(chapter.id)}
                            onMouseLeave={() => setActiveChapter(null)}
                        >
                            <button
                                onClick={() => onChapterClick(chapter.id)}
                                className={`w-full text-xl font-medium text-left p-2 focus:outline-none 
                                    ${status?.isUnlocked ? 'focus:ring-2 focus:ring-golden-yellow' : 'cursor-not-allowed'}`}
                                disabled={!status?.isUnlocked}
                            >
                                <span className="mr-2 text-[#13193F] font-bold">{chapter.id}</span>
                                {chapter.title}
                                {!status?.isUnlocked && (
                                    <Lock
                                        className="inline-block ml-2 text-terracotta"
                                        size={16}
                                        strokeWidth={2.5}
                                        style={{
                                            filter: 'drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.3))',
                                            transform: 'translateY(2px)'
                                        }}
                                    />
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const coordinates = [
    { x: 25.1, y: 53.6 },
    { x: 26, y: 57 },
    { x: 21.8, y: 47.3 },
    { x: 19.4, y: 50 },
    { x: 21.6, y: 53.8 },
    { x: 24.6, y: 48.3 },
    { x: 34.2, y: 78.8 },
    { x: 24.7, y: 19 },
    { x: 40.2, y: 44.9 },
    { x: 12.1, y: 41.2 },
    { x: 11, y: 53.5 },
    { x: 24.5, y: 63.7 }
];


const Map: React.FC<MapProps> = ({ onAreaClick, activeChapter, setActiveChapter }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const updateMapScale = () => {
            if (mapRef.current) {
                const containerWidth = mapRef.current.offsetWidth;
                const containerHeight = mapRef.current.offsetHeight;

                // Scale to 80% of the container's dimensions
                const scaleX = (containerWidth * 0.75) / 1920;
                const scaleY = (containerHeight * 0.75) / 1080;

                // Use the smaller scale to ensure the image fits within the container
                const newScale = Math.min(scaleX, scaleY);

                // Calculate offsets to center the scaled image
                const newOffsetX = 0
                const newOffsetY = 0

                setScale(newScale);
                setOffset({ x: newOffsetX, y: newOffsetY });
            }
        };

        updateMapScale();
        window.addEventListener('resize', updateMapScale);
        return () => window.removeEventListener('resize', updateMapScale);
    }, []);

    return (
        <div ref={mapRef} className='absolute inset-0 overflow-hidden'
            style={{
                position: 'absolute',
                width: '1920px',
                height: '1080px',
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                left: `${offset.x}px`,
                top: `${offset.y}px`,
                backgroundImage: "url('/images/GreeceMap.png')",
                backgroundSize: 'cover',
            }}>
            {coordinates.map((coord, index) => (
                <div
                    key={index}
                    className="absolute"
                    style={{
                        left: `${coord.x}%`,
                        top: `${coord.y}%`,
                        transform: 'translate(-50%, -50%)'
                    }}
                >
                    {/*active chapter is set here*/}
                    <button
                        className={`w-6 h-6 flex items-center justify-center font-bold text-xl transition-all duration-200 transform font-cinzel
                        ${activeChapter === index + 1
                                ? 'scale-150 text-greek-black'
                                : 'text-white hover:text-greek-black hover:scale-150'
                            }`}
                        onClick={() => onAreaClick(index)} //link to next page
                        onMouseEnter={() => setActiveChapter(index + 1)}
                        onMouseLeave={() => setActiveChapter(null)}
                    >
                        {index + 1}
                    </button>
                </div>
            ))}
        </div>
    )
}

interface ChapterProgress {
    id: number;
    isUnlocked: boolean;
    path: string;
}

// Add this outside the component
const unlockedChapters: ChapterProgress[] = [
    {
        id: 0,
        isUnlocked: true,
        path: "/greek-text"
    },
    {
        id: 1,
        isUnlocked: false,
        path: "/greek-text-2"
    }
];


const VideoTransition: React.FC = () => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const navigate = useNavigate();  // Add this line
    const [videoEnded, setVideoEnded] = useState(false); // Tracks when Globe.mp4 has finished playing
    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [activeChapter, setActiveChapter] = useState<number | null>(null);
    const [showTransitionVideo, setShowTransitionVideo] = useState(false); // Controls which video is displayed - Globe.mp4 or transition video
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            videoElement.addEventListener('ended', handleVideoEnd);
            return () => {
                videoElement.removeEventListener('ended', handleVideoEnd);
            };
        }
    }, []);


    const handleAreaClick = (index: number) => {
        const chapter = unlockedChapters.find(ch => ch.id === index);

        if (chapter?.isUnlocked) {
            setPopupMessage('Navigating...');
            setShowPopup(true);
            setTimeout(() => {
                setShowPopup(false);
                setShowTransitionVideo(true);
            }, 1000);
        } else {
            setPopupMessage(`Chapter ${index + 1} is not available yet`);
            setShowPopup(true);
            setTimeout(() => {
                setShowPopup(false);
            }, 2000);
        }
    };

    const handleChapterClick = (id: number) => {
        handleAreaClick(id - 1);
    };

    const handleVideoEnd = () => {
        setVideoEnded(true);
    };

    return (
        <div className="relative w-screen h-screen overflow-hidden"
            style={{ backgroundColor: '#487DBB' }}>
            {/* Conditional render: switches between transition video and globe/map view */}
            {showTransitionVideo ? (
                // Shows Hercules growing up video and navigates to greek-text
                <TransitionVideo
                    destinationPath="/greek-text"
                />
            ) : (
                <>
                    {/* Initial globe video - plays first due to autoPlay */}
                    <video
                        className="absolute inset-0 w-full h-full object-cover"
                        autoPlay
                        ref={videoRef}
                        muted
                    >
                        <source src="/videos/Globe.mp4" type="video/mp4" />
                    </video>
                    {/* Show map and table only after globe video ends */}
                    {videoEnded && (
                        <Map
                            onAreaClick={handleAreaClick} // Triggers transition video on valid click
                            activeChapter={activeChapter}
                            setActiveChapter={setActiveChapter}
                        />
                    )}
                    {/* Show chapter selection after globe video ends */}
                    {videoEnded && <TableOfContents
                        onChapterClick={handleChapterClick}
                        activeChapter={activeChapter}
                        setActiveChapter={setActiveChapter}
                    />}
                    {showPopup && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-black bg-opacity-75 text-white px-4 py-2 rounded">
                                {popupMessage}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default VideoTransition;

function setActiveChapter(id: number): void {
    throw new Error('Function not implemented.');
}
